import { Controller } from "@hotwired/stimulus"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"
import { show_error, show_success, htmlEscape, dataTruncate } from "lib/helpers"

// Example on `app/views/users/index.haml`:
//   .padded-tb.users_list{ data: { controller: "users--list", ... } }
//     %table#users_list{ data: { "users--list_target" => "table" } }

export default class extends Controller {
  static targets = ["table"]

  static values = {
    removeUsersPath: String,
    exportUsersPath: String,
    contactsPath: String,
    usersRootPath: String,
    currentApp: { type: String, default: "" },
    currentPage: { type: Number, default: 0 },
  }

  connect() {
    this.#flashStashedMessage()
    if (!this.hasTableTarget) return

    this.#initDataTable()
    this.#bindCheckboxChanges()
  }

  disconnect() {
    if (this.dataTable) {
      this.dataTable = destroyTurboDataTable(this.dataTable)
    }
    if (this.theadEl && this.theadClickHandler) {
      this.theadEl.removeEventListener("click", this.theadClickHandler, true)
    }
    this.theadEl = null
    this.theadClickHandler = null

    $("#users_list_wrapper").off(".usersList")
    $("#check-all").off(".usersList")

    this.pages = null
    this.sorting = null
  }

  remove(event) {
    if (event) event.preventDefault()

    const groupName = $("input#check-all").val()
    const checkboxes = $(`input[name^='${groupName}']:checked`)
    const available = $(`input[name^='${groupName}']`)
    const removingAll = checkboxes.length === available.length

    if (!available.length) return
    if (!checkboxes.length) {
      alert("Please select at least one item")
      return
    }
    if (!confirm("Are you sure? Removing selected users will also delete associated smart chat assistants, and this action cannot be reversed. Proceed with caution.")) {
      return
    }

    $(".dataTables_processing").css("display", "block")

    const ids = $.makeArray(checkboxes.map(function () { return $(this).val() }))

    $.delete(this.removeUsersPathValue, { item_ids: ids }, (response) => {
      if (response && response.error === true) {
        show_error(response.message)
      } else {
        show_success("user has been successfully removed")

        const settings = this.dataTable.settings()[0]
        if (removingAll && (settings._iRecordsDisplay - settings._iDisplayStart <= settings._iDisplayLength)) {
          this.dataTable.page("previous")
        }
      }
      this.dataTable.draw(false)
    })

    if (removingAll) {
      $("input#check-all").click()
    }
  }

  exportSelected(event) {
    if (event) event.preventDefault()
    this.#export(false)
  }

  exportAll(event) {
    if (event) event.preventDefault()
    this.#export(true)
  }

  #flashStashedMessage() {
    if (typeof localStorage === "undefined") return
    const msg = localStorage.getItem("mymessage")
    if (msg === null) return
    if (typeof show_success === "function") {
      show_success(msg)
    }
    localStorage.removeItem("mymessage")
  }

  #initDataTable() {
    const $table = $(this.tableTarget)

    this.dataTable = createTurboDataTable($table, {
      dom: '<"H"lfr>t<"F"ip<"toolbar">>',
      scrollX: true,
      bPaginate: true,
      pagingType: "full_numbers",
      order: [1, "asc"],
      stateSave: true,
      serverSide: true,
      processing: true,
      searchDelay: 2000,
      language: {
        searchPlaceholder: "ID, Login, Full name, Email",
      },
      ajax: {
        url: this.usersRootPathValue || "users",
        type: "POST",
        data: (d) => {
          d.previous_first_entity_id_on_the_page = $table.find("tr").eq(1).find("td").eq(1).find("span").text()
          d.previous_last_entity_id_on_the_page = $table.find("tr").last().find("td").eq(1).find("span").text()
          d.previous_page = $(".paginate_button.active").text()
          d.pages = this.pages
          d.previous_per_page = $table.find("tr").length
        },
        beforeSend: () => this.#blockPagination(),
        complete: () => this.#unblockPagination(),
      },
      stateLoadParams: (settings, data) => {
        data.search.search = ""
      },
      initComplete: (settings) => {
        $("#loading").hide()
        if ($("#reader").val() === "false") {
          this.#installToolbar()
        }
        new $.fn.dataTable.Api(settings).columns.adjust()
      },
      columnDefs: this.#columnDefs(),
    })

    this.dataTable.page("first")

    $table.on("page.dt", () => {
      this.pages = this.dataTable.page.info().pages
    })

    $table.on("length.dt", () => {
      this.dataTable.page("first")
    })

    this.theadEl = this.element.querySelector("thead")
    if (this.theadEl) {
      this.theadClickHandler = (event) => {
        if (this.sorting === "sorting") {
          event.stopPropagation()
          return false
        }
      }
      this.theadEl.addEventListener("click", this.theadClickHandler, true)
    }
  }

  #installToolbar() {
    const $toolbar = $(".toolbar")
    $toolbar.html('<br><br><a href="#" class="remove js-remove-users">Remove selected</a>')
    $toolbar.find(".js-remove-users").on("click", (e) => this.remove(e))

    if ($("#export_enabled").val() === "true") {
      const $selected = $('<a href="#">Export selected</a>').on("click", (e) => this.exportSelected(e))
      const $all = $('<a href="#">Export all</a>').on("click", (e) => this.exportAll(e))
      $toolbar.append("&nbsp;&nbsp;").append($selected).append("&nbsp;&nbsp;").append($all)
    }
  }

  #blockPagination() {
    const search = $('input[type="search"]')[0]
    const lengthSelect = $("select[name='users_list_length']")[0]
    if (search) search.disabled = true
    if (lengthSelect) lengthSelect.disabled = true
    this.sorting = "sorting"
    $("#users_list_paginate").hide()
  }

  #unblockPagination() {
    const search = $('input[type="search"]')[0]
    const lengthSelect = $("select[name='users_list_length']")[0]
    if (search) search.disabled = false
    if (lengthSelect) lengthSelect.disabled = false
    $("#users_list_paginate").show()
    this.sorting = "done"
    $("li.paginate_button.active a").click(function () { return false })
  }

  #columnDefs() {
    const usersRoot = this.usersRootPathValue
    const contactsRoot = this.contactsPathValue

    return [
      {
        targets: 0,
        createdCell(cell, cellData) {
          if (cellData) {
            $(cell).html(
              `<input type="checkbox" class="table-checkbox-row" id="check--users${cellData}" name="item_ids[]" value="${cellData}"/> <label for="check--users${cellData}"><label/>`,
            )
          } else {
            $(cell).empty()
          }
        },
      },
      {
        targets: 1,
        name: "id",
        createdCell(cell, cellData) {
          $(cell).html(`<a href='${usersRoot}/edit/${cellData}'>${dataTruncate(cellData, 15)}</a>`)
        },
      },
      {
        targets: 2,
        name: "login",
        createdCell(cell, cellData) {
          if (cellData == 0) {
            $(cell).html("")
          } else {
            $(cell).html(dataTruncate(htmlEscape(cellData), 15))
          }
        },
      },
      {
        targets: 3,
        name: "full_name",
        createdCell(cell, cellData) {
          $(cell).html(dataTruncate(cellData, 20))
        },
      },
      {
        targets: 4,
        name: "email",
        createdCell(cell, cellData) {
          $(cell).html(dataTruncate(cellData, 20))
        },
      },
      {
        targets: 5,
        name: "external_user_id",
        createdCell(cell, cellData) {
          $(cell).html(dataTruncate(cellData, 10))
        },
      },
      {
        targets: 6,
        name: "facebook_id",
        createdCell(cell, cellData) {
          $(cell).html(dataTruncate(cellData, 10))
        },
      },
      {
        targets: 7,
        createdCell(cell, cellData) {
          $(cell).html(dataTruncate(cellData, 20))
        },
      },
      {
        targets: 8,
        name: "last_request_at",
        createdCell(cell, cellData) {
          if (cellData) {
            $(cell).html(moment(cellData.replace(" UTC", "")).format())
          } else {
            $(cell).html("")
          }
        },
      },
      {
        targets: 9,
        name: "created_at",
        createdCell(cell, cellData) {
          $(cell).html(moment(cellData).format())
        },
      },
      {
        targets: 10,
        name: "contacts",
        createdCell(cell, cellData, rowData) {
          if ($("#reader").val() === "false") {
            $(cell).html(`<a href='${contactsRoot}/list/${rowData[0]}'>${dataTruncate(cellData)}</a>`)
          } else {
            $(cell).html(cellData)
          }
        },
      },
      { targets: [0, 7, 10], sortable: false },
    ]
  }

  #bindCheckboxChanges() {
    $("#users_list_wrapper").on("change.usersList", ".table-checkbox-row", function () {
      const isChecked = $(this).prop("checked")
      $(this).closest("tr").toggleClass("selected-row", isChecked)

      const selectedCount = $(".table-checkbox-row:checked").length
      const totalCount = $(".table-checkbox-row").length
      $("#check-all").prop("checked", selectedCount > 0 && selectedCount === totalCount)
    })

    $("#check-all").on("change.usersList", function () {
      const isChecked = $(this).prop("checked")
      $(".table-checkbox-row").prop("checked", isChecked).closest("tr").toggleClass("selected-row", isChecked)
    })
  }

  #export(checkedAll) {
    const groupName = $("input#check-all").val()
    const checkboxes = $(`input[name^='${groupName}']:checked`)
    const available = $(`input[name^='${groupName}']`)

    if (!available.length) return
    if (!checkboxes.length && !checkedAll) {
      alert("Please select at least one item")
      return
    }

    const ids = $.makeArray(checkboxes.map(function () { return $(this).val() }))

    $("#get_users_profile_iframe").remove()
    $("body").append(
      `<iframe id="get_users_profile_iframe" src='${this.exportUsersPathValue}?item_ids=${ids}&checked_all=${checkedAll}' style='display: none;'></iframe>`,
    )

    $("#get_users_profile_iframe").on("load", function () {
      const respData = $("#get_users_profile_iframe").contents().find("body").text()
      if (!respData) return
      try {
        const response = jQuery.parseJSON(respData)
        if (response.error === true) {
          show_error(response.message)
        }
      } catch (e) {
      }
    })
  }
}
